import Box from "@memorang/ui/components/Box";
import EmptyState from "@memorang/ui/components/EmptyView";
import { CmeTabContent } from "@memorang/ui/components/LessonDetailCmeTab/CmeTabContent";
import { StatsTabContent } from "@memorang/ui/components/LessonDetailStatsTab/StatsTabContent";
import { LessonDetailBottomBar } from "@memorang/ui/components/LessonDetailViewScreen/LessonDetailBottomBar";
import { LessonDetailViewScreen } from "@memorang/ui/components/LessonDetailViewScreen/LessonDetailViewScreen";
import { ModuleArtifactsSection } from "@memorang/ui/components/LessonDetailViewScreen/ModuleArtifactsSection";
import type { ModuleArtifactAction } from "@memorang/ui/components/LessonDetailViewScreen/types";
import { useRouter } from "expo-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	exploreModules,
	moduleDetailArtifacts,
	moduleDetailMetaChips,
	moduleDetailMilestones,
	moduleDetailMiniStats,
	moduleDetailProgressPercent,
} from "../constants";

type Props = {
	moduleId: string;
};

export const ModuleDetailContainer = ({ moduleId }: Props) => {
	const router = useRouter();
	const { t } = useTranslation();

	const module = useMemo(
		() => exploreModules.find((item) => item.id === moduleId),
		[moduleId],
	);

	const handleArtifactPress = () => {
		//TODO: navigate to artifact once module content wiring lands
	};
	const handleTakeQuizPress = () => {
		//TODO: start module quiz once session wiring lands
	};

	const artifactActions: Record<string, ModuleArtifactAction> = {
		overview: {
			label: t("lessons.detail.artifact.readAgainCta"),
			mode: "text",
		},
		"imaging-the-knee": {
			label: t("lessons.detail.artifact.listenAgainCta"),
			mode: "text",
		},
		"study-guide": {
			label: t("lessons.detail.artifact.startCta"),
			mode: "contained",
		},
	};

	const artifacts = moduleDetailArtifacts.map((artifact) => ({
		...artifact,
		action: artifactActions[artifact.id],
	}));

	const milestones = moduleDetailMilestones.map((milestone) =>
		milestone.status === "in-progress"
			? {
					...milestone,
					action: {
						label: t("lessons.stats.milestones.startCta"),
						onPress: handleArtifactPress,
					},
				}
			: milestone,
	);

	const nextActivityBottomBar = (
		<LessonDetailBottomBar onPress={handleTakeQuizPress} />
	);

	const tabs = [
		{
			label: t("lessons.detail.tabs.content"),
			icon: "text-box-outline",
			content: (
				<ModuleArtifactsSection
					artifacts={artifacts}
					onArtifactPress={handleArtifactPress}
				/>
			),
			bottomBar: nextActivityBottomBar,
		},
		{
			label: t("lessons.detail.tabs.insights"),
			icon: "chart-box-outline",
			content: (
				<StatsTabContent
					miniStats={moduleDetailMiniStats}
					milestones={milestones}
				/>
			),
			bottomBar: nextActivityBottomBar,
		},
		{
			label: t("lessons.detail.tabs.cme"),
			icon: "certificate-outline",
			content: (
				<CmeTabContent
					state="in-progress"
					title={t("lessons.cme.inProgress.title")}
					description={t("lessons.cme.inProgress.description")}
					footnote={t("lessons.cme.incompleteFootnote")}
					action={{
						label: t("lessons.cme.inProgress.cta"),
						onPress: handleTakeQuizPress,
					}}
				/>
			),
		},
	];

	if (!module) {
		return (
			<Box flex={1} justifyContent="center">
				<EmptyState
					title="Module not found"
					subtitle="This module is unavailable. Head back and pick another one."
				/>
			</Box>
		);
	}

	return (
		<LessonDetailViewScreen
			title={module.title}
			description={module.description}
			progressPercent={moduleDetailProgressPercent}
			tabs={tabs}
			metaChips={moduleDetailMetaChips}
			coverImage={module.thumbnailUrl}
			onBackPress={() => router.back()}
		/>
	);
};
